import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { replayCounter } from '@/core/engine/x01/replay';
import { deriveScore } from '@/core/engine/x01/game';
import { uidSchema, type Match, type TeamId } from '@/core/schemas';
import { useAuth } from '@/app/providers/auth/authContext';
import { describeError } from '@/features/team/describeError';
import { sideLabel } from '@/features/matches/matchPresentation';
import { useTournamentData } from '@/features/tournament/useTournamentData';
import { setResultAsDirector } from '@/infrastructure/repositories/matchRepository';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { Alert } from '@/shared/ui/Alert';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';

import { DirectorResultForm } from './DirectorResultForm';
import { describeBlocked } from './progressionNotice';
import { useAdminActions } from './useAdminActions';

export function AdminConflictsPage() {
  usePageTitle('Konflikte');
  const { session } = useAuth();
  const { tournament, teams, matches, lookup, rulesFor, isLoading, errorMessage } =
    useTournamentData();
  const actions = useAdminActions();
  const [resolved, setResolved] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const actorUid = session.status === 'authenticated' ? uidSchema.parse(session.account.uid) : null;

  /**
   * Sets the result and immediately reconciles the bracket.
   *
   * The Cloud Function does the same on its own, but it is not guaranteed to be
   * deployed (docs/SECURITY.md §6.2) — and a correction is precisely the case
   * where a director must not be left wondering whether the tree caught up.
   * Reconciling on the locally updated match rather than on the subscription's
   * copy avoids racing the snapshot; both paths are idempotent.
   */
  const resolve = useMutation({
    mutationFn: async (input: {
      match: Match;
      winnerTeamId: TeamId | null;
      legs: { home: number; away: number };
    }) => {
      if (actorUid === null) throw new Error('Nicht angemeldet');

      const updated = await setResultAsDirector({ ...input, actorUid });

      if (tournament === undefined) {
        return null;
      }

      const patched = matches.map((match) => (match.id === updated.id ? updated : match));
      return actions.reconcile(tournament, teams, patched);
    },
    onSuccess: (outcome, variables) => {
      setResolved(variables.match.id);
      setWarning(
        outcome === null ? null : describeBlocked(outcome.progression.blocked, matches, lookup),
      );
    },
  });

  if (isLoading) {
    return <LoadingScreen label="Konflikte werden geladen …" />;
  }

  if (errorMessage !== null) {
    return <PageState title="Konflikte nicht verfügbar" description={errorMessage} />;
  }

  const conflicts = matches.filter((match) => match.status === 'conflict');
  const completed = matches.filter((match) => match.status === 'completed');

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-lg font-semibold">Konflikte ({conflicts.length})</h2>
      {resolved !== null && (
        <Alert tone="success">
          Das Ergebnis wurde übernommen und der Turnierbaum fortgeschrieben.
        </Alert>
      )}
      {warning !== null && <Alert tone="warning">{warning}</Alert>}
      {conflicts.length === 0 && (
        <Alert tone="success">Derzeit gibt es keine offenen Konflikte.</Alert>
      )}

      {conflicts.map((match) => {
        const rules = rulesFor(match);
        const game =
          match.counter !== null && rules !== null ? replayCounter(match.counter, rules) : null;
        const score = game === null ? null : deriveScore(game);

        return (
          <section key={match.id} className="border-danger/40 bg-surface rounded-xl border p-5">
            <h3 className="font-semibold">
              {sideLabel(match.home, lookup)} gegen {sideLabel(match.away, lookup)}
            </h3>

            <p className="text-fg-muted mt-2 text-sm">
              {score === null
                ? 'Es wurde noch nicht gezählt.'
                : `Gezählter Stand: ${score.legs.home}:${score.legs.away} Legs${
                    score.sets !== null ? ` · ${score.sets.home}:${score.sets.away} Sätze` : ''
                  }. Ein Teamleiter hat das Ergebnis angezweifelt.`}
            </p>

            <div className="mt-4">
              <p className="text-fg-subtle mb-3 text-xs uppercase">
                Endgültiges Ergebnis festlegen
              </p>
              <DirectorResultForm
                match={match}
                rules={rules}
                lookup={lookup}
                isSubmitting={resolve.isPending}
                errorMessage={describeError(resolve.error)}
                onSubmit={(result) => resolve.mutate({ match, ...result })}
              />
            </div>
          </section>
        );
      })}

      {completed.length > 0 && (
        <details className="border-line bg-surface rounded-xl border p-5">
          <summary className="cursor-pointer text-sm font-semibold">
            Beendetes Spiel korrigieren ({completed.length})
          </summary>
          <p className="text-fg-subtle mt-2 text-xs">
            Nur nötig, wenn ein bereits bestätigtes Ergebnis nachweislich falsch ist.
          </p>
          <ul className="mt-4 flex flex-col gap-4">
            {completed.map((match) => (
              <li key={match.id} className="border-line border-t pt-4">
                <p className="mb-3 text-sm font-medium">
                  {sideLabel(match.home, lookup)} {match.result?.score.legs.home}:
                  {match.result?.score.legs.away} {sideLabel(match.away, lookup)}
                </p>
                <DirectorResultForm
                  match={match}
                  rules={rulesFor(match)}
                  lookup={lookup}
                  isSubmitting={resolve.isPending}
                  errorMessage={describeError(resolve.error)}
                  onSubmit={(result) => resolve.mutate({ match, ...result })}
                />
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
